import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Avatar, IconButton } from "@mui/material";
import {
  ArrowForward as NextIcon,
  ArrowBack as PrevIcon,
} from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import heic2any from "heic2any";

const ContactCard = ({ name, title, bio, image, email, bioLink, linkedIn }) => {
  const [linesToShow, setLinesToShow] = useState(3);
  const [convertedImage, setConvertedImage] = useState(image);

  const bioRef = useRef(null);

  const calculateLines = () => {
    if (bioRef.current) {
      const lineHeight = parseFloat(
        window.getComputedStyle(bioRef.current).lineHeight
      );
      const containerHeight = bioRef.current.clientHeight;
      const lines = Math.floor(containerHeight / lineHeight);
      setLinesToShow(lines);
    }
  };
  useEffect(() => {
    const convertIfHeic = async () => {
      try {
        if (
          typeof image === "string" &&
          image.toLowerCase().endsWith(".heic")
        ) {
          const response = await fetch(image);
          const blob = await response.blob();
          const converted = await heic2any({ blob, toType: "image/jpeg" });
          setConvertedImage(URL.createObjectURL(converted));
        } else {
          setConvertedImage(image);
        }
      } catch (error) {
        console.error("HEIC conversion failed:", error);
        setConvertedImage(image);
      }
    };

    if (image) convertIfHeic();
  }, [image]);
  useEffect(() => {
    calculateLines();
    window.addEventListener("resize", calculateLines);
    return () => window.removeEventListener("resize", calculateLines);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        marginBottom: "20px",
        padding: "10px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
        maxWidth: "auto",
        alignItems: {
          xs: "center",
          sm: "flex-start",
          md: "flex-start",
          lg: "flex-start",
        },
        textAlign: { xs: "center", sm: "left", md: "left", lg: "left" },
        flexDirection: { xs: "column", sm: "row", md: "row", lg: "row" },
      }}
    >
      <Box
        sx={{ marginRight: "20px", marginTop: "10px", marginBottom: "10px" }}
      >
        <Avatar
          src={convertedImage}
          alt="Profile picture"
          sx={{
            width: 150,
            height: 150,
          }}
          variant="rounded"
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: { xs: "center", sm: "left", lg: "left" },
            flexWrap: "wrap",
            gap: 1,
            mb: { xs: 1.5, sm: 1, lg: 1 },
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {name}
          </Typography>

          {bioLink && (
            <IconButton
              color="primary"
              size="small"
              href={bioLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          )}

          {linkedIn && (
            <IconButton
              color="primary"
              size="small"
              href={linkedIn}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedInIcon fontSize="small" />
            </IconButton>
          )}

          {email && (
            <IconButton color="primary" size="small" href={`mailto:${email}`}>
              <EmailIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Typography variant="body1" sx={{ marginBottom: "10px" }}>
          {title}
        </Typography>

        <Box
          sx={{
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-line",
              lineHeight: 1.8,
              textAlign: "justify",
            }}
          >
            {bio}
          </Typography>
        </Box>

        <Box
          sx={{ display: "flex", justifyContent: "center", marginTop: "10px" }}
        >
          <Box
            sx={{
              width: "10px",
              height: "10px",
              margin: "0 5px",
              borderRadius: "50%",
              backgroundColor: "#FFD700",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ContactCard;
